import { useEffect, useMemo, useState, useRef } from "react";
import {
    CalendarDays,
    Car,
    CheckCircle2,
    ChevronRight,
    CreditCard,
    Landmark,
    LockKeyhole,
    MapPin,
    ReceiptText,
    ShieldCheck,
    Smartphone,
    UserRound,
    Users,
    WalletCards,
} from "lucide-react";
import {
    createPaymentOrder,
    verifyPayment,
    getBookingJourney,
    fetchTripDetails,
    completeBooking
} from "../../../services/restapiService";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { decodeJourneyId } from "../../../utils/url";
import { motion } from "motion/react";

const API_ORIGIN = "https://api.gosehari.com";

const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    })}`;

const formatDate = (value) => {
    if (!value) return "Date pending";

    return new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    });
};


const FieldPreview = ({ icon: Icon, label, value }) => (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2 text-xs font-bold uppercase text-slate-400">
            <Icon size={15} />
            {label}
        </div>
        <div className="mt-2 truncate text-sm font-bold text-slate-900">
            {value || "Not provided"}
        </div>
    </div>
);

const SummaryRow = ({ label, value, strong }) => (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 text-sm">
        <span className={strong ? "font-bold text-slate-950" : "font-medium text-slate-500"}>
            {label}
        </span>
        <span className={strong ? "font-bold text-slate-950" : "font-bold text-slate-800"}>
            {value}
        </span>
    </div>
);

function PaymentPage() {


    const { journeyId: encodedJourneyId } = useParams();
    const contactDetailsRef = useRef(null);
    const journeyId = encodedJourneyId
        ? decodeJourneyId(encodedJourneyId)
        : null;


    const [bookingData, setBookingData] = useState(null);
    const [tripData, setTripData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});

    useEffect(() => {


        const loadJourney = async () => {
            try {
                setLoading(true);

                // journeyId is now the decoded ID
                const res = await getBookingJourney(journeyId);

                if (res.data.success && res.data.data) {
                    const journey = res.data.data;

                    setBookingData({
                        ...journey,
                        email: localStorage.getItem("userEmail") || journey.email || "",
                    });

                    const token = localStorage.getItem("token");

                    const tripRes = await fetchTripDetails(
                        token,
                        journey.tour_id
                    );

                    setTripData(tripRes.data);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        if (journeyId) {
            loadJourney();
        }
    }, [journeyId]);

    const scrollToContactDetails = () => {
        contactDetailsRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };


    const travelers = useMemo(() => {
        if (Array.isArray(bookingData?.travelers)) return bookingData.travelers;
        if (Array.isArray(bookingData?.traveller)) return bookingData.traveller;
        if (Array.isArray(bookingData?.booking_travelers)) return bookingData.booking_travelers;
        return [];
    }, [bookingData]);

    const bookingId = journeyId;



    const activityPrice = Number(bookingData?.activity_price || 0);

    const vehiclePrice = Number(
        bookingData?.vehicle_total_price || 0
    );

    const vat = Number(bookingData?.gst_amount || 0);

    const grandTotal = Number(bookingData?.total_price || 0);


    const totalGuests =
        Number(bookingData?.adults || 0) +
        Number(bookingData?.children || 0) +
        Number(bookingData?.infants || 0);

    const galleryImage = tripData?.gallery?.[0]?.image_path;
    const heroImage = galleryImage
        ? `${API_ORIGIN}${galleryImage}`
        : bookingData?.tour_image || "/landingpage/bg.jpg";


    const validateBooking = () => {
        const errors = {};

        if (!bookingData?.name?.trim()) {
            errors.name = "Full Name is required.";
        } else if (bookingData.name.trim().length < 3) {
            errors.name = "Full Name must be at least 3 characters.";
        } else if (!/^[A-Za-z\s]+$/.test(bookingData.name.trim())) {
            errors.name = "Full Name should contain only letters and spaces.";
        }

        if (!bookingData?.email?.trim()) {
            errors.email = "Email is required.";
        } else {
            const emailRegex =
                /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

            if (!emailRegex.test(bookingData.email.trim())) {
                errors.email = "Please enter a valid email address.";
            }
        }

        if (!bookingData?.phone?.trim()) {
            errors.phone = "Phone Number is required.";
        } else {
            const phone = bookingData.phone.replace(/\D/g, "");

            if (!/^[6-9]\d{9}$/.test(phone)) {
                errors.phone = "Please enter a valid 10-digit mobile number.";
            }
        }

        return errors;
    };


    const handlePayment = async () => {
        const isVerified = localStorage.getItem("isVerified");
        const userEmail = localStorage.getItem("userEmail");



        if (!isVerified || !userEmail) {
            localStorage.setItem(
                "redirectAfterLogin",
                `/secure-checkout?journeyId=${journeyId}`
            );

            navigate("/payment-login", {
                state: {
                    journeyId,
                },
            });

            return;
        }


        if (!window.Razorpay) {
            alert("Payment gateway is still loading. Please try again.");
            return;
        }

        try {
            setPaying(true);

            const validationErrors = validateBooking();
            setErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) {
                scrollToContactDetails();
                return;
            }


            const orderRes = await createPaymentOrder({ journeyId });
            const order = orderRes.data.order;

            const options = {
                key: "rzp_live_SPVfNI3CGqi3Ky",
                amount: order.amount,
                currency: "INR",
                name: "GoSehari",
                description: bookingData?.tour_title || "Tour Booking",
                order_id: order.id,

                handler: async function (response) {
                    console.log("Payment Success");

                    const verifyRes = await verifyPayment(response);
                    console.log("verifyRes", verifyRes.data);

                    if (verifyRes.data.success) {

                        const bookingRes = await completeBooking({
                            journeyId,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            payment_method: "Razorpay",

                            name: bookingData.name,
                            email: bookingData.email,
                            phone: bookingData.phone,
                            city: bookingData.city,
                            billing_address: bookingData.billing_address,
                            message_contact: bookingData.message_contact,

                        });

                        console.log("bookingRes", bookingRes.data);

                        navigate(`/thank-you/${journeyId}`, {
                            replace: true
                        });
                    }
                },

                prefill: {
                    name: bookingData?.name || "",
                    email: bookingData?.email || userEmail || "",
                    contact: bookingData?.phone || "",
                },


                theme: {
                    color: "#0f766e",
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (error) {
            console.error(error);
            alert("Payment failed");
        } finally {
            setPaying(false);
        }
    };

    const [leadTraveler, setLeadTraveler] = useState({

    });



    const handleLeadTravelerChange = (e) => {
        const { name, value } = e.target;
        console.log("name", name);
        console.log("value", value);

        setLeadTraveler((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleBookingDataChange = (e) => {
        const { name, value } = e.target;

        setBookingData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };
    return (
        <main className="min-h-screen bg-[#f6f8fb] text-slate-900">
            <section className="relative overflow-hidden h-[220px] sm:h-[260px] lg:h-[300px]">
                {bookingData?.tour_image && (
                    <motion.img
                        src={bookingData.tour_image}
                        alt={bookingData?.tour_title || "Tour"}
                        initial={{ scale: 1.15 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 8, ease: "easeOut" }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                )}

                {/* Dark Overlay */}
                <div className="absolute inset-0 " />

                {/* Extra Blur Overlay */}

                {/* Floating Glow */}
                <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl animate-pulse" />
                <div className="absolute right-0 bottom-0 h-96 w-96 rounded-full bg-emerald-400/20 blur-3xl animate-pulse" />

                {/* Content */}
                <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-4 sm:px-6 lg:px-10">

                    <motion.div
                        initial={{ opacity: 20, y: 20 }}
                        animate={{ opacity: 100, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl"
                    >

                        <span className="inline-flex rounded-full bg-white/10 px-4 py-2 text-sm text-white backdrop-blur-md border border-white/20">
                            ✈ Premium Travel Experience
                        </span>

                        <h3 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight leading-tight text-white lg:text-3xl">
                            Checkout
                        </h3>

                        <p className="mt-3 max-w-2xl text-sm sm:text-base lg:text-lg leading-6 lg:leading-8 text-slate-200">
                            Review your selected date, guests, vehicles, and traveller details
                            before proceeding to the secure payment gateway.
                        </p>

                    </motion.div>
                </div>
            </section>

            <section className="container px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
                    <div className="space-y-6">
                        <article className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6" ref={contactDetailsRef}>
                            <div className="mb-5 flex items-center gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                                    <UserRound size={21} />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl font-bold text-slate-950">Contact Details</h2>
                                    <p className="text-sm text-slate-500">
                                        Please review your contact information carefully before completing your payment.
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Full Name */}
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">
                                        Full Name
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={bookingData?.name || ""}
                                        onChange={handleBookingDataChange}
                                        className={`w-full rounded-xl border px-4 py-3 text-sm ${errors.name
                                            ? "border-red-500 focus:ring-red-200"
                                            : "border-slate-300 focus:border-teal-500 focus:ring-teal-200"
                                            }`}
                                    />

                                    {errors.name && (
                                        <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                                    )}
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        readOnly
                                        value={bookingData?.email || ""}
                                        onChange={handleBookingDataChange}
                                        placeholder="Enter your email"
                                        className="w-full rounded-xl border border-slate-300 px-4 py-3 bg-gray-100 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
                                    />
                                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[0.675rem] font-medium text-red-700">
                                        <span> Booking updates will be sent to this email.</span>
                                    </div>



                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={bookingData?.phone || ""}
                                        onChange={handleBookingDataChange}
                                        placeholder="Enter your phone number"
                                        className={`w-full rounded-xl border px-4 py-3 text-sm ${errors.phone
                                            ? "border-red-500 focus:ring-red-200"
                                            : "border-slate-300 focus:border-teal-500 focus:ring-teal-200"
                                            }`}
                                    />

                                    {errors.phone && (
                                        <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
                                    )}

                                </div>

                                {/* Billing City */}
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">
                                        Billing City
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        onChange={handleBookingDataChange}
                                        placeholder="Enter your city"
                                        className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
                                    />
                                </div>
                            </div>

                            {/* Billing Address */}
                            <div className="mt-4">
                                <label className="mb-1 block text-sm font-medium text-slate-700">
                                    Billing Address
                                </label>
                                <textarea
                                    name="billing_address"
                                    rows={4}
                                    onChange={handleBookingDataChange}
                                    placeholder="Enter your billing address"
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
                                />
                            </div>


                            {/* message */}
                            <div className="mt-4">
                                <label className="mb-1 block text-sm font-medium text-slate-700">
                                    Messsage
                                </label>
                                <textarea
                                    name="message_contact"
                                    rows={4}
                                    onChange={handleBookingDataChange}
                                    placeholder="Enter your message"
                                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-200"
                                />
                            </div>
                        </article>

                        <article className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                            <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                                        <WalletCards size={21} />
                                    </div>
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-bold text-slate-950">Payment Method</h2>
                                        <p className="text-sm text-slate-500">Choose your preferred option in Razorpay.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {[
                                    [CreditCard, "Cards", "Credit and debit cards"],
                                    [Smartphone, "UPI", "Apps and QR payments"],
                                    [Landmark, "Net banking", "All major banks"],
                                ].map(([Icon, title, detail]) => (
                                    <div
                                        key={title}
                                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                                    >
                                        <Icon size={22} className="text-teal-700" />
                                        <div className="mt-3 text-sm font-bold text-slate-950">{title}</div>
                                        <div className="mt-1 text-xs font-medium leading-5 text-slate-500">{detail}</div>
                                    </div>
                                ))}
                            </div>
                        </article>
                    </div>

                    <aside className="lg:sticky lg:top-24">
                        <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-xl shadow-slate-950/10">
                            <div className="bg-slate-950 p-5 text-white">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4">
                                    <div>
                                        <h2 className="text-lg sm:text-xl font-bold">Order Summary</h2>
                                        <p className="mt-1 text-sm text-slate-300">Booking ID {bookingId || "pending"}</p>
                                    </div>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                                        <ReceiptText size={23} />
                                    </div>
                                </div>
                            </div>









                            <article className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm">
                                <div className="">
                                    <div className="p-5 sm:p-6">
                                        <h3 className="text-lg font-bold leading-tight text-slate-950">
                                            {bookingData?.tour_title || "Your selected tour"}
                                        </h3>




                                        <div className="divide-y divide-slate-200">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 py-1 pt-4 text-sm">
                                                <span className="font-medium text-slate-600">
                                                    Selected Date
                                                </span>
                                                <span className="font-bold text-slate-950">
                                                    {bookingData?.selected_date &&
                                                        new Date(bookingData.selected_date).toLocaleDateString("en-GB", {
                                                            timeZone: "UTC",
                                                        })}
                                                </span>
                                            </div>
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 py-1 pb-4 text-sm">
                                                <span className="font-medium text-slate-600">
                                                    Selected Time
                                                </span>
                                                <span className="font-bold text-slate-950">
                                                    {bookingData?.selected_time || "Time Not Selected"}

                                                </span>
                                            </div>

                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 py-1 text-sm">
                                                <span className="font-medium text-slate-600">
                                                    Total Travelers
                                                </span>
                                                <span className="font-bold text-slate-950">
                                                    {totalGuests}
                                                </span>
                                            </div>

                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 py-1 text-sm">
                                                <span className="font-medium text-slate-600">
                                                    Selected Package
                                                </span>
                                                <span className="font-bold text-slate-950">
                                                    {bookingData?.package_name}
                                                </span>
                                            </div>

                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 py-1 text-sm">
                                                <span className="font-medium text-slate-600">
                                                    Package / Per Person
                                                </span>
                                                <span className="font-bold text-slate-950">
                                                    {bookingData?.activity_price_per_person}
                                                </span>
                                            </div>
                                        </div>







                                        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
                                            {[
                                                ["Adults", bookingData?.adults || 0],
                                                ["Children", bookingData?.children || 0],
                                                ["Infants", bookingData?.infants || 0],
                                            ].map(([label, value]) => (
                                                <div key={label} className="rounded-2xl bg-slate-50 p-3 text-center">
                                                    <div className="text-lg sm:text-xl font-bold text-slate-950">{value}</div>
                                                    <div className="mt-1 text-xs font-bold uppercase text-slate-400">{label}</div>
                                                </div>
                                            ))}
                                        </div>



                                        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 mb-4">
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-slate-200 px-4 py-3">
                                                <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                                                    <Car size={17} className="text-teal-700" />
                                                    Selected vehicles
                                                </div>
                                                <span className="text-sm font-bold text-slate-950">
                                                    {formatCurrency(vehiclePrice)}
                                                </span>
                                            </div>

                                            {bookingData?.vehicles?.length > 0 ? (
                                                <div className="divide-y divide-slate-200">
                                                    {bookingData.vehicles.map((vehicle, index) => (
                                                        <div
                                                            key={`${vehicle.vehicle_id || vehicle.vehicle_name}-${index}`}
                                                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 gap-4 px-4 py-3 text-sm">
                                                            <span className="font-medium text-slate-600">
                                                                {vehicle.vehicle_name}
                                                            </span>
                                                            <span className="font-bold text-slate-950">
                                                                {formatCurrency(vehicle.vehicle_price)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="px-4 py-4 text-sm font-medium text-slate-500">
                                                    No vehicle selected
                                                </div>
                                            )}
                                        </div>










                                        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <SummaryRow label="Guest Price" value={formatCurrency(activityPrice)} />
                                            <SummaryRow label="Vehicle price" value={formatCurrency(vehiclePrice)} />
                                            <SummaryRow label="VAT (18%)" value={formatCurrency(vat)} />
                                        </div>





                                        <div className="rounded-2xl bg-teal-50 p-4 mt-4">
                                            <SummaryRow label="Total payable" value="INR" strong />
                                            <div className="mt-2 text-3xl font-bold text-teal-800">
                                                {formatCurrency(grandTotal)}
                                            </div>
                                        </div>



                                        <button
                                            type="button"
                                            onClick={handlePayment}
                                            disabled={loading || paying || !journeyId}
                                            className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-teal-700 px-5 text-base font-bold text-white shadow-lg shadow-teal-900/20 transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {paying ? "Opening payment..." : `Pay`}
                                            <ChevronRight size={19} />
                                        </button>

                                        <div className="flex mt-4 items-start gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 p-3 text-xs font-semibold leading-5 text-emerald-800">
                                            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                                            Your payment is encrypted and processed securely by Razorpay.
                                        </div>







                                    </div>
                                </div>

                            </article>


                            <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t shadow-2xl px-4 py-3 z-50">

                                <div className="flex items-center justify-between">

                                    <div>
                                        <p className="text-xs text-gray-500">
                                            Total
                                        </p>

                                        <h2 className="text-xl font-bold">
                                            {formatCurrency(grandTotal)}
                                        </h2>
                                    </div>

                                    <button
                                        onClick={handlePayment}
                                        disabled={loading || paying || !journeyId}
                                        className="bg-teal-700 text-white px-6 py-3 rounded-xl font-semibold"
                                    >
                                        {paying ? "Loading..." : "Pay Now"}
                                    </button>

                                </div>

                            </div>







                        </div>
                    </aside>
                </div>
            </section>
        </main>
    );
}

export default PaymentPage;
